import { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, ArrowRight, Send, Share2, Info } from "lucide-react";
import { OrderProductSelect } from "@/components/order/OrderProductSelect";
import { OrderAtlasConfig, type AtlasConfig } from "@/components/order/OrderAtlasConfig";
import { OrderValidConfig, type ValidConfig } from "@/components/order/OrderValidConfig";
import { OrderAdresstwattForm, type AdresstwattContact } from "@/components/order/OrderAdresstwattForm";
import { OrderUserDetails, type UserDetail } from "@/components/order/OrderUserDetails";
import {
  OrderCustomerForm,
  type ContactInfo,
  type CompanyInfo,
  type BillingInfo,
} from "@/components/order/OrderCustomerForm";
import { OrderReview, type EditableSection } from "@/components/order/OrderReview";
import { OrderSummary } from "@/components/order/OrderSummary";
import { OrderConfirmation } from "@/components/order/OrderConfirmation";
import { PageSEO } from "@/components/seo/PageSEO";

import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

const EMPTY_USER: UserDetail = { name: "", email: "", role: "" };

function createUsers(count: number, existing: UserDetail[]): UserDetail[] {
  if (count <= 0) return [];
  const result: UserDetail[] = [];
  for (let i = 0; i < count; i++) {
    result.push(existing[i] ?? { ...EMPTY_USER });
  }
  return result;
}

export default function Bestall() {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [isSharedConfig, setIsSharedConfig] = useState(false);
  const [isFullShare, setIsFullShare] = useState(false);

  const [step, setStep] = useState(0);
  const [product, setProduct] = useState<"atlas" | "valid" | "adresstwatt" | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [orderNumber, setOrderNumber] = useState<number | undefined>();

  // Atlas config
  const [atlasConfig, setAtlasConfig] = useState<AtlasConfig>({
    plan: "small",
    adminUsers: 1,
    viewerUsers: 0,
    maps: 1,
    countries: ["se"],
    routing: "none",
  });

  // Valid config
  const [validConfig, setValidConfig] = useState<ValidConfig>({
    plan: "small",
    addons: [],
    webPortal: false,
    webPortalUsers: [],
  });

  // Load shared config from URL on mount
  useEffect(() => {
    const configParam = searchParams.get("config");
    if (!configParam) return;
    try {
      const decoded = JSON.parse(atob(decodeURIComponent(configParam)));
      if (!decoded.product || !["atlas", "valid"].includes(decoded.product)) return;
      setProduct(decoded.product);
      if (decoded.product === "atlas" && decoded.atlas) {
        setAtlasConfig((prev) => ({ ...prev, ...decoded.atlas }));
        if (decoded.atlas.adminUsers) setAdminUsers(createUsers(decoded.atlas.adminUsers, []));
        if (decoded.atlas.viewerUsers) setViewerUsers(createUsers(decoded.atlas.viewerUsers, []));
      }
      if (decoded.product === "valid" && decoded.valid) {
        setValidConfig((prev) => ({ ...prev, ...decoded.valid }));
      }

      if (decoded.full) {
        // Full share: load all customer data and jump to review
        if (decoded.contact) setContact(decoded.contact);
        if (decoded.company) setCompany(decoded.company);
        if (decoded.billing) setBilling(decoded.billing);
        if (decoded.product === "atlas") {
          if (decoded.adminUsers) setAdminUsers(decoded.adminUsers);
          if (decoded.viewerUsers) setViewerUsers(decoded.viewerUsers);
          // Atlas review step = 4
          setStep(4);
        } else {
          // Valid review step = 3
          setStep(3);
        }
        setIsFullShare(true);
      } else {
        setStep(1);
      }

      setIsSharedConfig(true);
      // Clean URL
      window.history.replaceState({}, "", window.location.pathname);
    } catch {
      // Invalid config, ignore silently
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  const [adresstwattContact, setAdresstwattContact] = useState<AdresstwattContact>({
    companyName: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
  });

  // User details
  const [adminUsers, setAdminUsers] = useState<UserDetail[]>([{ ...EMPTY_USER }]);
  const [viewerUsers, setViewerUsers] = useState<UserDetail[]>([]);

  // Customer form
  const [contact, setContact] = useState<ContactInfo>({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
  });
  const [company, setCompany] = useState<CompanyInfo>({
    companyName: "",
    orgNumber: "",
    address: "",
    postalCode: "",
    city: "",
    country: "SE",
    email: "",
    phone: "",
  });
  const [billing, setBilling] = useState<BillingInfo>({
    address: "",
    postalCode: "",
    city: "",
    country: "SE",
    reference: "",
    poNumber: "",
    invoiceMethod: "email",
    invoiceEmail: "",
    vatNumber: "",
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [editingSection, setEditingSection] = useState<EditableSection>(null);

  const isAdresstwatt = product === "adresstwatt";
  const needsUserStep = product === "atlas";
  // Adresstwatt: step 0=product, step 1=form (submit directly) → totalSteps=2
  // Atlas: 0=product, 1=config, 2=users, 3=customer, 4=review → 5
  // Valid: 0=product, 1=config, 2=customer, 3=review → 4
  const totalSteps = isAdresstwatt ? 2 : needsUserStep ? 5 : 4;

  const handleProductSelect = (p: "atlas" | "valid" | "adresstwatt") => {
    setProduct(p);
    setStep(1);
  };

  const handleAtlasConfigChange = useCallback(
    (cfg: AtlasConfig) => {
      setAtlasConfig(cfg);
      setAdminUsers((prev) => createUsers(cfg.adminUsers, prev));
      setViewerUsers((prev) => createUsers(cfg.viewerUsers, prev));
    },
    []
  );

  const handleShare = useCallback((full = false) => {
    const config: Record<string, unknown> = { product };
    if (product === "atlas") config.atlas = atlasConfig;
    if (product === "valid") config.valid = validConfig;

    if (full) {
      config.full = true;
      config.contact = contact;
      config.company = company;
      config.billing = billing;
      if (product === "atlas") {
        config.adminUsers = adminUsers;
        config.viewerUsers = viewerUsers;
      }
    }

    const encoded = encodeURIComponent(btoa(JSON.stringify(config)));
    const url = `${window.location.origin}/bestall?config=${encoded}`;
    navigator.clipboard.writeText(url).then(() => {
      toast({
        title: full ? t("order.shareFullCopied") : t("order.shareCopied"),
        description: full ? t("order.shareFullCopiedDesc") : t("order.shareCopiedDesc"),
      });
    });
  }, [product, atlasConfig, validConfig, contact, company, billing, adminUsers, viewerUsers, t]);

  const customerStep = isAdresstwatt ? -1 : needsUserStep ? 3 : 2;
  const reviewStep = isAdresstwatt ? 1 : needsUserStep ? 4 : 3;

  const canProceed = useMemo(() => {
    if (step === 0) return false;

    // Adresstwatt: step 1 is the form — validate contact fields + terms
    if (isAdresstwatt && step === 1) {
      return (
        adresstwattContact.companyName.trim() &&
        adresstwattContact.firstName.trim() &&
        adresstwattContact.lastName.trim() &&
        adresstwattContact.email.trim() &&
        adresstwattContact.phone.trim() &&
        termsAccepted
      );
    }

    if (step === 1) {
      if (product === "atlas") return atlasConfig.countries.length > 0;
      if (product === "valid") {
        // If web portal is checked, all users must have name + email
        if (validConfig.webPortal && validConfig.webPortalUsers.length > 0) {
          const allValid = validConfig.webPortalUsers.every(u => u.name.trim() && u.email.trim());
          if (!allValid) return false;
        }
        return true;
      }
      return true;
    }
    if (needsUserStep && step === 2) {
      return adminUsers.every((u) => u.name.trim() && u.email.trim());
    }
    if (step === customerStep) {
      const orgValid = /^\d{10}$/.test(company.orgNumber.replace(/[- ]/g, ""));
      return (
        contact.firstName.trim() &&
        contact.lastName.trim() &&
        contact.phone.trim() &&
        contact.email.trim() &&
        company.companyName.trim() &&
        orgValid &&
        company.address.trim() &&
        company.postalCode.trim() &&
        company.city.trim() &&
        billing.address.trim() &&
        billing.postalCode.trim() &&
        billing.city.trim() &&
        (billing.invoiceMethod === "letter" || billing.invoiceEmail.trim()) &&
        termsAccepted
      );
    }
    if (step === reviewStep) return !isFullShare || termsAccepted;
    return false;
  }, [step, product, atlasConfig, adminUsers, contact, company, billing, termsAccepted, needsUserStep, customerStep, reviewStep, isAdresstwatt, adresstwattContact]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      let orderData: Record<string, unknown>;

      if (isAdresstwatt) {
        orderData = {
          product: "adresstwatt",
          plan_name: "adresstwatt",
          configuration: { message: adresstwattContact.message },
          user_details: [],
          contact: {
            firstName: adresstwattContact.firstName,
            lastName: adresstwattContact.lastName,
            email: adresstwattContact.email,
            phone: adresstwattContact.phone,
          },
          company: { ...company, companyName: adresstwattContact.companyName },
          billing,
        };
      } else {
        orderData = {
          product: product!,
          plan_name: product === "atlas" ? atlasConfig.plan : validConfig.plan,
          configuration: product === "atlas" ? atlasConfig : validConfig,
          user_details: product === "atlas"
            ? [...adminUsers, ...viewerUsers]
            : validConfig.webPortal
              ? validConfig.webPortalUsers.map(u => ({ ...u, type: "webPortal" }))
              : [],
          contact,
          company,
          billing,
        };
      }

      const { data, error } = await supabase.from("orders").insert(orderData as any).select("order_number").single();
      if (error) throw error;

      const insertedOrderNumber = data?.order_number;
      setOrderNumber(insertedOrderNumber);

      try {
        await supabase.functions.invoke("send-order-email", {
          body: { ...orderData, order_number: insertedOrderNumber, language: i18n.language },
        });
      } catch {
        // Non-critical
      }

      setSubmitted(true);
    } catch (err) {
      console.error("Order submission error:", err);
      toast({
        title: t("order.errorTitle"),
        description: t("order.errorDesc"),
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const goBack = () => {
    if (step === 1) {
      setProduct(null);
      setStep(0);
    } else {
      setStep((s) => s - 1);
    }
  };

  const goForward = () => {
    if (isAdresstwatt && step === 1) {
      handleSubmit();
    } else if (step === reviewStep) {
      handleSubmit();
    } else {
      setStep((s) => s + 1);
    }
  };

  if (submitted) {
    return (
      <div className="container mx-auto px-4 py-24 max-w-3xl">
        <PageSEO
          title={t("order.pageTitle")}
          description={t("seo.bestall")}
        />
        <OrderConfirmation
          orderNumber={orderNumber}
          product={product || undefined}
          contactEmail={isAdresstwatt ? adresstwattContact.email : contact.email}
        />
      </div>
    );
  }

  const renderStepContent = () => {
    if (step === 0) {
      return <OrderProductSelect onSelect={handleProductSelect} />;
    }

    // Adresstwatt flow: only step 1 (the form)
    if (isAdresstwatt && step === 1) {
      return (
        <OrderAdresstwattForm
          contact={adresstwattContact}
          company={company}
          billing={billing}
          termsAccepted={termsAccepted}
          onContactChange={setAdresstwattContact}
          onCompanyChange={setCompany}
          onBillingChange={setBilling}
          onTermsChange={setTermsAccepted}
        />
      );
    }

    if (step === 1) {
      return product === "atlas" ? (
        <OrderAtlasConfig config={atlasConfig} onChange={handleAtlasConfigChange} />
      ) : (
        <OrderValidConfig config={validConfig} onChange={setValidConfig} />
      );
    }
    if (needsUserStep && step === 2) {
      return (
        <OrderUserDetails
          adminCount={atlasConfig.adminUsers}
          viewerCount={atlasConfig.viewerUsers}
          adminUsers={adminUsers}
          viewerUsers={viewerUsers}
          onChangeAdmin={setAdminUsers}
          onChangeViewer={setViewerUsers}
        />
      );
    }
    if (step === customerStep) {
      return (
        <OrderCustomerForm
          contact={contact}
          company={company}
          billing={billing}
          termsAccepted={termsAccepted}
          onContactChange={setContact}
          onCompanyChange={setCompany}
          onBillingChange={setBilling}
          onTermsChange={setTermsAccepted}
        />
      );
    }
    // Review step
    return (
      <OrderReview
        product={product as "atlas" | "valid"}
        atlasConfig={atlasConfig}
        validConfig={validConfig}
        contact={contact}
        company={company}
        billing={billing}
        adminUsers={adminUsers}
        viewerUsers={viewerUsers}
        showTerms={isFullShare}
        termsAccepted={termsAccepted}
        onTermsChange={setTermsAccepted}
        editingSection={editingSection}
        onEditSection={setEditingSection}
        onAtlasConfigChange={handleAtlasConfigChange}
        onValidConfigChange={setValidConfig}
        onAdminUsersChange={setAdminUsers}
        onViewerUsersChange={setViewerUsers}
        onContactChange={setContact}
        onCompanyChange={setCompany}
        onBillingChange={setBilling}
      />
    );
  };

  const progressPercent = ((step) / (totalSteps - 1)) * 100;

  return (
    <div className="container mx-auto px-4 py-24 max-w-6xl">
      <PageSEO
        title={t("order.pageTitle")}
        description={t("order.pageDescription")}
      />

      {/* Shared config banner */}
      {isSharedConfig && step > 0 && (
        <div className="mb-6 flex items-start gap-3 rounded-lg border border-primary/20 bg-primary/5 p-4">
          <Info className="h-5 w-5 text-primary mt-0.5 shrink-0" />
          <p className="text-sm text-foreground">{t(isFullShare ? "order.sharedFullBanner" : "order.sharedBanner")}</p>
        </div>
      )}

      {/* Progress — only shown after product is selected */}
      {step > 0 && (
        <div className="mb-8 space-y-2">
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>
              {t("order.step")} {step} / {totalSteps - 1}
            </span>
          </div>
          <Progress value={progressPercent} className="h-2" />
        </div>
      )}

      <div className={`grid ${step > 0 && !isAdresstwatt ? "lg:grid-cols-3" : ""} gap-8`}>
        {/* Main content */}
        <div className={step > 0 && !isAdresstwatt ? "lg:col-span-2" : ""}>
          {renderStepContent()}

          {/* Navigation */}
          {step > 0 && (
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
            <div className="flex items-center gap-2">
              {step !== reviewStep && (
                <Button variant="outline" onClick={goBack} className="gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  {t("order.back")}
                </Button>
              )}
              {step === 1 && !isAdresstwatt && !isSharedConfig && (
                <Button variant="outline" onClick={() => handleShare(false)} className="gap-2">
                  <Share2 className="h-4 w-4" />
                  {t("order.share")}
                </Button>
              )}
              {step >= 2 && !isAdresstwatt && !isSharedConfig && (
                <Button variant="outline" onClick={() => handleShare(true)} className="gap-2">
                  <Share2 className="h-4 w-4" />
                  {t("order.shareOrder")}
                </Button>
              )}
            </div>
              <Button
                onClick={goForward}
                disabled={!canProceed || submitting}
                className="gap-2 bg-coral text-coral-foreground hover:bg-coral/90"
              >
                {(isAdresstwatt && step === 1) || step === reviewStep ? (
                  <>
                    {submitting
                      ? t("order.submitting")
                      : isAdresstwatt
                        ? t("order.adresstwatt.submitRequest")
                        : t("order.submitOrder")}
                    <Send className="h-4 w-4" />
                  </>
                ) : (
                  <>
                    {t("order.next")}
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          )}
        </div>

        {/* Sidebar summary — not shown for adresstwatt */}
        {step > 0 && !isAdresstwatt && (
          <div className="lg:col-span-1">
            <OrderSummary
              product={product}
              atlasConfig={atlasConfig}
              validConfig={validConfig}
            />
          </div>
        )}
      </div>
    </div>
  );
}
